import { useEffect, useState, useCallback } from "react";
import { Search, X, Image as ImageIcon, Loader2, AlertCircle } from "lucide-react";
import { adsApi } from "./api";
import { useLanguage } from "../contexts/LanguageContext";

/**
 * Pexels stock photo picker modal.
 * Call onSelect(url) with the large-size URL when user clicks a thumbnail.
 */
export default function StockPhotoPicker({ onSelect, onClose }) {
  const { t } = useLanguage();
  const [query, setQuery] = useState("");
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [configured, setConfigured] = useState(true);

  const loadCurated = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await adsApi.get("/stock/curated", { params: { per_page: 30 } });
      setConfigured(r.data.configured !== false);
      setPhotos(r.data.photos || []);
    } catch (e) {
      const raw = e?.response?.data?.detail;
      if (raw && /login required/i.test(String(raw))) {
        setError(t.err_login_required || "Login required for this action");
      } else {
        setError(raw || (t.stock_load_failed || "Failed to load"));
      }
    } finally { setLoading(false); }
  }, [t.err_login_required, t.stock_load_failed]);

  // Map backend's English error strings to translated versions.
  function localizedError(raw) {
    if (!raw) return raw;
    const s = String(raw);
    if (/login required/i.test(s)) return t.err_login_required || "Login required for this action";
    return s;
  }

  async function search() {
    if (!query.trim()) return loadCurated();
    setLoading(true);
    setError(null);
    try {
      const r = await adsApi.post("/stock/search", { query: query.trim(), per_page: 30 });
      setConfigured(r.data.configured !== false);
      setPhotos(r.data.photos || []);
    } catch (e) {
      setError(localizedError(e?.response?.data?.detail) || (t.stock_search_failed || "Search failed"));
    } finally { setLoading(false); }
  }

  useEffect(() => { loadCurated(); }, [loadCurated]);

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)",
        zIndex: 100, display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        data-testid="stock-photo-picker"
        style={{
          width: "100%", maxWidth: 840, maxHeight: "85vh",
          background: "#13131e", borderRadius: 14,
          border: "1px solid rgba(255,255,255,0.1)",
          display: "flex", flexDirection: "column", overflow: "hidden",
        }}
      >
        <header style={{
          padding: "14px 18px", borderBottom: "1px solid rgba(255,255,255,0.08)",
          display: "flex", alignItems: "center", gap: 10,
        }}>
          <ImageIcon size={18} style={{ color: "#c084fc" }} />
          <div style={{ color: "#fff", fontSize: 15, fontWeight: 800, flex: 1 }}>
            {t.stock_title || "Stock photos"}
          </div>
          <button onClick={onClose} style={{
            background: "transparent", border: 0, color: "#8b8ba7",
            cursor: "pointer", padding: 4,
          }} data-testid="stock-picker-close" aria-label="Close">
            <X size={18} />
          </button>
        </header>

        <div style={{ padding: "12px 18px", display: "flex", gap: 8 }}>
          <div style={{ position: "relative", flex: 1 }}>
            <Search size={16} style={{
              position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)",
              color: "#8b8ba7",
            }} />
            <input
              data-testid="stock-picker-input"
              type="text" value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && search()}
              placeholder={t.stock_placeholder || "nature, city, food, abstract…"}
              style={{
                width: "100%", padding: "10px 12px 10px 34px",
                background: "rgba(0,0,0,0.3)", color: "#fff",
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: 8, fontSize: 14, boxSizing: "border-box",
              }}
            />
          </div>
          <button
            data-testid="stock-picker-search"
            onClick={search}
            style={{
              padding: "10px 16px",
              background: "linear-gradient(135deg,#a855f7,#7c3aed)",
              border: 0, borderRadius: 8, color: "#fff", fontWeight: 800, cursor: "pointer",
            }}
          >{t.stock_search || "Search"}</button>
        </div>

        <div style={{
          flex: 1, overflowY: "auto", padding: "4px 18px 18px",
        }}>
          {!configured && (
            <div data-testid="stock-picker-not-configured" style={{
              padding: 20, background: "rgba(251,146,60,0.1)",
              border: "1px solid rgba(251,146,60,0.3)",
              borderRadius: 10, color: "#fdba74", fontSize: 13,
              display: "flex", gap: 10, alignItems: "flex-start",
            }}>
              <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
              <div>
                <div style={{ fontWeight: 800, marginBottom: 4 }}>{t.stock_not_configured || "Stock photos not configured"}</div>
                <div style={{ color: "rgba(255,255,255,0.7)" }}>
                  Admin: set <code>PEXELS_API_KEY</code> in backend <code>.env</code> (free at pexels.com/api).
                </div>
              </div>
            </div>
          )}

          {loading && (
            <div style={{ color: "#8b8ba7", textAlign: "center", padding: 40 }}>
              <Loader2 className="animate-spin" size={24} />
            </div>
          )}

          {!loading && error && !configured === false && (
            <div style={{ color: "#fca5a5", padding: 20, textAlign: "center" }}>{error}</div>
          )}

          {!loading && configured && photos.length === 0 && (
            <div style={{ color: "#8b8ba7", padding: 40, textAlign: "center", fontSize: 14 }}>
              {t.stock_no_results || "No results. Try another search term."}
            </div>
          )}

          <div style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill,minmax(160px,1fr))",
            gap: 8, marginTop: 12,
          }}>
            {photos.map((p) => (
              <button
                key={p.id}
                type="button"
                data-testid={`stock-photo-${p.id}`}
                onClick={() => onSelect(p.large || p.preview)}
                title={`By ${p.photographer}`}
                style={{
                  padding: 0, border: "1px solid rgba(255,255,255,0.08)",
                  borderRadius: 8, overflow: "hidden", cursor: "pointer",
                  background: p.avg_color || "#222", aspectRatio: "1 / 1",
                  position: "relative",
                }}
              >
                <img src={p.thumb || p.preview} alt=""
                  loading="lazy"
                  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
                />
                <span style={{
                  position: "absolute", left: 0, right: 0, bottom: 0,
                  padding: "6px 8px",
                  background: "linear-gradient(to top,rgba(0,0,0,0.7),transparent)",
                  color: "#fff", fontSize: 10, fontWeight: 700,
                  textAlign: "left",
                  textShadow: "0 1px 2px rgba(0,0,0,0.5)",
                }}>{p.photographer}</span>
              </button>
            ))}
          </div>
          {configured && (
            <div style={{ color: "#555", fontSize: 11, textAlign: "center", padding: "16px 0 4px" }}>
              {t.stock_provided_by || "Photos provided by"} <a href="https://pexels.com" target="_blank" rel="noopener noreferrer"
                style={{ color: "#888" }}>Pexels</a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
